/**
 * Servicio centralizado de funciones para los reproductores de audio
 * Este archivo contiene todas las funciones compartidas entre los diferentes
 * reproductores para evitar duplicación de código.
 */

/**
 * Decodifica entidades HTML en un texto
 * @param {string} input - Texto con entidades HTML
 * @returns {string} Texto decodificado
 */
export const htmlDecode = (input) => {
  // No crear un elemento DOM real, usar un método más eficiente
  if (!input) return "";

  // Usar una solución más eficiente con mapeo de entidades comunes
  const entityMap = {
    "&amp;": "&",
    "&lt;": "<",
    "&gt;": ">",
    "&quot;": '"',
    "&#39;": "'",
    "&#x2F;": "/",
    "&#x60;": "`",
    "&#x3D;": "=",
  };

  return input.replace(
    /&amp;|&lt;|&gt;|&quot;|&#39;|&#x2F;|&#x60;|&#x3D;/g,
    function (entity) {
      return entityMap[entity] || entity;
    }
  );
};

/**
 * Detecta si el dispositivo tiene pantalla táctil
 * @returns {boolean} Verdadero si es un dispositivo táctil
 */
export const isTouchScreenDevice = (() => {
  // Calcular una sola vez y guardar el resultado
  let isTouchDevice;

  return () => {
    // Si ya se calculó, devolver el resultado memorizado
    if (isTouchDevice !== undefined) {
      return isTouchDevice;
    }

    // Calcular si es la primera vez
    try {
      isTouchDevice =
        "ontouchstart" in window ||
        navigator.maxTouchPoints > 0 ||
        navigator.msMaxTouchPoints > 0;
      return isTouchDevice;
    } catch {
      isTouchDevice = false;
      return false;
    }
  };
})();

/**
 * Limpia el título de una canción eliminando texto entre paréntesis, corchetes y llaves
 * @param {string} text - Texto a limpiar
 * @returns {string} Texto limpio
 */
const cleanTitle = (text) => {
  if (!text) return "";

  // Ya no eliminamos texto entre paréntesis, ahora simplemente limpiamos espacios múltiples
  return text.replace(/\s+/g, " ").trim();
};

/**
 * Genera una URL basada en el proxy si está habilitado
 * @param {string} baseUrl - URL base
 * @returns {object} URL y opciones de fetch
 */
const getProxyUrl = (baseUrl) => {
  console.log(`🌐 getProxyUrl para: ${baseUrl}`);
  console.log(`🔧 REACT_APP_USE_PROXY: ${process.env.REACT_APP_USE_PROXY}`);
  console.log(`🔧 REACT_APP_PROXY_URL: ${process.env.REACT_APP_PROXY_URL}`);

  const url = process.env.REACT_APP_USE_PROXY
    ? process.env.REACT_APP_PROXY_URL + baseUrl
    : baseUrl;

  const fetchOptions = process.env.REACT_APP_USE_PROXY
    ? {}
    : { mode: "no-cors" };

  console.log(`🔄 URL transformada: ${url}`);
  console.log(`🔄 Opciones de fetch:`, fetchOptions);

  return { url, fetchOptions };
};

/**
 * Detecta si parece ser una cuña o anuncio de la radio
 * @param {string} singer - Nombre del artista
 * @param {string} song - Nombre de la canción
 * @returns {boolean} True si parece ser una cuña
 */
const isBroadcastAnnouncement = (singer, song) => {
  if (!singer && !song) return true;

  // Obtener el nombre de la estación actual
  const stationName = getStationNameFromUrl().toLowerCase();

  // Eliminar espacios para comparación (versión sin espacios)
  const singerNoSpaces = singer ? singer.replace(/\s+/g, "").toLowerCase() : "";
  const songNoSpaces = song ? song.replace(/\s+/g, "").toLowerCase() : "";
  const stationNameNoSpaces = stationName.replace(/\s+/g, "");

  // Verificar si artista sin espacios está en nombre de estación
  if (singerNoSpaces && stationNameNoSpaces.includes(singerNoSpaces)) {
    console.log(
      `📢 Detectada cuña porque artista sin espacios "${singerNoSpaces}" está en nombre de estación "${stationNameNoSpaces}"`
    );
    return true;
  }

  // Verificar si canción sin espacios está en nombre de estación
  if (songNoSpaces && stationNameNoSpaces.includes(songNoSpaces)) {
    console.log(
      `📢 Detectada cuña porque canción sin espacios "${songNoSpaces}" está en nombre de estación "${stationNameNoSpaces}"`
    );
    return true;
  }

  return false;
};

/**
 * Construye la consulta de búsqueda a partir de artista y canción
 * @param {string} singer - Nombre del artista/cantante
 * @param {string} song - Nombre de la canción
 * @returns {string} Consulta formateada para búsqueda
 */
export const buildSearchQuery = (singer, song) => {
  let searchQuery = "";

  if (singer && song) {
    searchQuery = `${singer} ${song}`;
  } else if (singer) {
    searchQuery = singer;
  } else if (song) {
    searchQuery = song;
  }

  return searchQuery.trim();
};

// Cache para resultados de búsqueda de iTunes
const itunesCache = {};
const ITUNES_CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 horas en milisegundos

/**
 * Función auxiliar para limpiar términos de búsqueda para mejor compatibilidad
 * @param {string} text - Texto a normalizar
 * @returns {string} Texto normalizado
 */
const normalizeSearchTerm = (text) => {
  if (!text) return "";

  // Eliminar los símbolos de comillas más comunes que causan problemas en búsquedas
  return text
    .replace(/['′'']/g, "")
    .replace(/["″""]/g, "")
    .replace(/\s+/g, " ")
    .trim();
};

/**
 * Función auxiliar para buscar en iTunes con un algoritmo multinivel avanzado
 * @param {string} searchTerm - Término de búsqueda original (artista y/o canción)
 * @param {function} callback - Función callback con resultado
 */
const searchItunesAndResolve = (searchTerm, callback) => {
  console.log(`🔍 BÚSQUEDA INICIADA: "${searchTerm}"`);

  if (!searchTerm) {
    console.log("❌ Término de búsqueda vacío");
    callback(null);
    return;
  }

  // Usar un término de búsqueda normalizado para la caché
  const normalizedSearchTerm = searchTerm.toLowerCase().trim();

  // Verificar si ya tenemos resultados en caché
  if (itunesCache[normalizedSearchTerm]) {
    const cachedData = itunesCache[normalizedSearchTerm];

    // Verificar si la caché aún es válida (menos de 24 horas)
    if (Date.now() - cachedData.timestamp < ITUNES_CACHE_EXPIRY) {
      // Solo usar la caché si hay un resultado exitoso
      if (cachedData.result) {
        console.log(`✅ Resultado encontrado en caché: Imagen encontrada`);
        callback(cachedData.result);
        return;
      } else {
        console.log(`🔄 Resultado nulo en caché, reintentando búsqueda`);
        // Continuar con la búsqueda en caso de resultado nulo
      }
    } else {
      // Si la caché expiró, eliminarla
      console.log("🔄 Caché expirada, se hará nueva búsqueda");
      delete itunesCache[normalizedSearchTerm];
    }
  }

  // Variables para almacenar los intentos de búsqueda
  let searchAttempts = [];
  let currentAttemptIndex = 0;

  // Función para generar términos de búsqueda alternativos
  const generateSearchVariants = (term) => {
    if (!term) return [];

    console.log(`🔍 Generando variantes para: "${term}"`);

    // Forzar más variantes incluso cuando no podemos dividir artista/canción
    let variants = [term]; // Siempre incluir el término original

    // Normalizar para mejor compatibilidad con API
    const normalizedTerm = normalizeSearchTerm(term);
    if (normalizedTerm !== term) {
      variants.push(normalizedTerm);
    }

    // Dividir el término entre artista y canción
    let artist = "",
      song = "";

    // Intentar detectar varios patrones
    if (term.includes(" - ")) {
      [artist, song] = term.split(" - ").map((s) => s.trim());
      console.log(
        `📋 Patrón detectado " - ": Artista="${artist}", Canción="${song}"`
      );
    } else if (term.includes(": ")) {
      [artist, song] = term.split(": ").map((s) => s.trim());
      console.log(
        `📋 Patrón detectado ": ": Artista="${artist}", Canción="${song}"`
      );
    } else if (term.includes(" – ")) {
      [artist, song] = term.split(" – ").map((s) => s.trim());
      console.log(
        `📋 Patrón detectado " – ": Artista="${artist}", Canción="${song}"`
      );
    } else if (term.includes("-")) {
      [artist, song] = term.split("-").map((s) => s.trim());
      console.log(
        `📋 Patrón detectado "-": Artista="${artist}", Canción="${song}"`
      );
    } else {
      // Si no hay separador, intentar buscar patrones comunes
      const featPatterns = [
        / feat\.? /i,
        / ft\.? /i,
        / featuring /i,
        / & /i,
        / y /i,
        / and /i,
        / con /i,
      ];
      for (const pattern of featPatterns) {
        if (pattern.test(term)) {
          // Si tiene un patrón de colaboración, dividir por el primer espacio como aproximación
          const parts = term.split(" ");
          if (parts.length > 2) {
            // Usar la primera palabra como posible artista y el resto como canción
            artist = parts[0];
            song = parts.slice(1).join(" ");
            console.log(
              `📋 Patrón de colaboración detectado: Artista="${artist}", Canción="${song}"`
            );
            break;
          }
        }
      }

      // Si todavía no tenemos artista/canción, usar todo como artista
      if (!artist && !song) {
        artist = term;
        console.log(
          `📋 Sin separador detectado, usando todo como artista: "${artist}"`
        );
      }
    }

    // Agregar variantes optimizadas para versiones y remixes
    if (artist && song) {
      variants.push(`${artist} ${song}`);

      // Versión con artista y título base de la canción, especial para remixes
      let baseTitle = song;

      // Si la canción parece ser un remix o versión especial, intentar extraer el título base
      if (
        song.match(/\(.*(?:remix|mix|version|extended|edit|vocal|club).*\)/i)
      ) {
        // Extraer la parte base de la canción (antes del paréntesis)
        const baseTitleMatch = song.match(/^(.*?)(?:\s*\(|\s*\[)/i);
        if (baseTitleMatch && baseTitleMatch[1]) {
          baseTitle = baseTitleMatch[1].trim();
          // Variante solo con el título base + artista
          if (baseTitle !== song) {
            variants.push(`${artist} ${baseTitle}`);
            console.log(`📋 Título base extraído para remix: "${baseTitle}"`);
          }
        }
      }

      // Agregar variantes solo con artista o canción
      variants.push(song);
      variants.push(artist);

      // Variante sin caracteres especiales
      const cleanSong = song
        .replace(/[^\w\s]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
      if (cleanSong !== song) variants.push(cleanSong);

      // Extraer artista principal (antes de featuring/&/and)
      const mainArtist = artist
        .split(/\s+(?:feat\.?|ft\.?|featuring|&|y|and|con)\s+/i)[0]
        .trim();
      if (mainArtist && mainArtist !== artist) {
        variants.push(`${mainArtist} ${song}`);
        variants.push(mainArtist);
      }

      // Limpiar paréntesis y símbolos de la canción
      const cleanSongWithoutParens = song
        .replace(/\([^)]*\)/g, "")
        .replace(/\[[^\]]*\]/g, "")
        .replace(/\{[^}]*\}/g, "")
        .replace(/\s+/g, " ")
        .trim();

      if (cleanSongWithoutParens && cleanSongWithoutParens !== song) {
        variants.push(`${artist} ${cleanSongWithoutParens}`);
        variants.push(cleanSongWithoutParens);

        if (mainArtist && mainArtist !== artist) {
          variants.push(`${mainArtist} ${cleanSongWithoutParens}`);
        }
      }

      // Variantes con reemplazo de caracteres especiales
      const expandedArtist = artist
        .replace(/ft\./g, "featuring")
        .replace(/feat\./g, "featuring")
        .replace(/&/g, "and");

      if (expandedArtist !== artist) {
        variants.push(`${expandedArtist} ${song}`);
        variants.push(`${expandedArtist} ${cleanSongWithoutParens}`);
      }
    } else if (artist) {
      // Si solo tenemos artista, intentar generar variantes
      const words = artist.split(/\s+/);

      // Si hay más de 3 palabras, podría ser un título de canción sin separador
      if (words.length > 3) {
        // Probar con las primeras palabras como artista y el resto como canción
        const possibleArtist = words.slice(0, 2).join(" ");
        const possibleSong = words.slice(2).join(" ");
        variants.push(`${possibleArtist} ${possibleSong}`);
        variants.push(possibleArtist);
        variants.push(possibleSong);
      }
    }

    // Incluir normalización sin acentos siempre
    const variantsWithoutAccents = variants.map((v) =>
      v.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    );

    // Combinar y eliminar duplicados
    const allVariants = [...variants, ...variantsWithoutAccents];
    const uniqueVariants = [
      ...new Set(allVariants.filter((v) => v.trim().length > 0)),
    ];

    console.log(
      `🔄 Se generaron ${uniqueVariants.length} variantes de búsqueda:`,
      uniqueVariants
    );
    return uniqueVariants;
  };

  // Generar variantes de búsqueda
  searchAttempts = generateSearchVariants(searchTerm);

  // Función para realizar búsqueda en iTunes con interpretación inteligente
  const performSearch = (currentTerm) => {
    console.log(`🔍 Intentando búsqueda con: "${currentTerm}"`);

    // Usar un proxy público para evitar CORS (más confiable)
    const proxiedItunesUrl = `https://corsproxy.io/?${encodeURIComponent(
      `https://itunes.apple.com/search?entity=song&limit=30&term=${encodeURIComponent(
        currentTerm
      )}`
    )}`;

    // Configurar un timeout para la solicitud
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error("Timeout")), 8000);
    });

    // Intentar con el proxy CORS
    Promise.race([fetch(proxiedItunesUrl), timeoutPromise])
      .then((response) => {
        console.log(
          `📥 Respuesta recibida de iTunes vía proxy: status=${response.status}`
        );
        if (!response.ok) {
          throw new Error(`Error de respuesta: ${response.status}`);
        }
        return response.json();
      })
      .then((json) => {
        const resultCount = json.results ? json.results.length : 0;
        console.log(
          `📊 iTunes devolvió ${resultCount} resultados para "${currentTerm}"`
        );

        if (!json.results || json.results.length === 0) {
          // No hay resultados, intentar siguiente variante
          console.log(
            `❌ Sin resultados para "${currentTerm}", probando siguiente variante`
          );
          tryNextVariant();
          return;
        }

        // Extraer palabras clave importantes del término de búsqueda
        const searchTermWords = currentTerm
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "") // Eliminar acentos
          .replace(/['",./#!$%&*;:{}=\-_`~()[\]]/g, " ") // Corregidos todos los escapes innecesarios
          .split(/\s+/)
          .filter((word) => word.length > 1 && !commonStopWords.includes(word));

        console.log("🔑 Palabras clave para puntuación:", searchTermWords);

        // Calcular relevancia para cada resultado
        const scoredResults = json.results.map((result) => {
          const artistName = result.artistName.toLowerCase();
          const trackName = result.trackName.toLowerCase();
          const combinedText = `${artistName} ${trackName}`.toLowerCase();

          // Puntaje base
          let score = 15; // Puntaje base más alto para aceptar más resultados

          // Aumentar puntuación por coincidencia exacta o contiene el término completo
          if (trackName.includes(currentTerm.toLowerCase())) {
            score += 50;
            console.log(
              `📈 +50 puntos para "${result.trackName}" - Coincidencia directa en título`
            );
          } else if (combinedText.includes(currentTerm.toLowerCase())) {
            score += 40;
            console.log(
              `📈 +40 puntos para "${result.trackName}" - Contiene término completo`
            );
          }

          // Coincidencia de palabras clave
          const matchedKeywords = searchTermWords.filter((keyword) => {
            // Buscar coincidencia directa o en texto normalizado
            const normalizedCombined = combinedText
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "");

            return (
              combinedText.includes(keyword) ||
              normalizedCombined.includes(keyword)
            );
          });

          // Porcentaje de palabras clave que coinciden
          const keywordMatchPercentage =
            searchTermWords.length > 0
              ? matchedKeywords.length / searchTermWords.length
              : 0;

          const keywordScore = keywordMatchPercentage * 45;
          score += keywordScore;

          if (keywordScore > 0) {
            console.log(
              `📈 +${keywordScore.toFixed(1)} puntos para "${
                result.trackName
              }" - Coincidencia de ${matchedKeywords.length}/${
                searchTermWords.length
              } palabras clave`
            );
          }

          // Penalización para karaoke/covers/remixes no deseados
          // (a menos que el término de búsqueda incluya esas palabras)
          const unwantedTerms = [
            "karaoke",
            "tribute",
            "cover",
            "instrumental version",
            "instrumental",
          ];
          if (
            !currentTerm
              .toLowerCase()
              .match(/(karaoke|tribute|cover|instrumental)/i)
          ) {
            for (const term of unwantedTerms) {
              if (trackName.includes(term) || artistName.includes(term)) {
                score -= 20; // Menos penalización
                console.log(
                  `📉 -20 puntos para "${result.trackName}" - Contiene término no deseado "${term}"`
                );
                break;
              }
            }
          }

          // Si es remix pero la búsqueda no menciona remix, penalizar ligeramente
          if (
            trackName.includes("remix") &&
            !currentTerm.toLowerCase().includes("remix")
          ) {
            score -= 10;
            console.log(
              `📉 -10 puntos para "${result.trackName}" - Es un remix no solicitado`
            );
          }

          return {
            result,
            score,
            debugInfo: {
              artistName,
              trackName,
              matchedKeywords,
              keywordMatchPercentage,
            },
          };
        });

        // Ordenar por puntuación y obtener el mejor resultado
        scoredResults.sort((a, b) => b.score - a.score);

        // Mostrar información de los 3 mejores resultados
        console.log("🏆 TOP 3 RESULTADOS:");
        scoredResults.slice(0, 3).forEach((item, index) => {
          console.log(
            `${index + 1}. "${item.result.artistName} - ${
              item.result.trackName
            }" (Score: ${item.score.toFixed(1)})`
          );
        });

        // Aceptar el resultado si tiene un puntaje decente (>15)
        if (scoredResults.length > 0 && scoredResults[0].score > 15) {
          const bestResult = scoredResults[0].result;
          const artworkUrl = bestResult.artworkUrl100.replace(
            "100x100",
            "800x800"
          );

          console.log(
            `✅ PORTADA ENCONTRADA para "${bestResult.artistName} - ${
              bestResult.trackName
            }" (Score: ${scoredResults[0].score.toFixed(1)})`
          );

          // Guardar resultado en caché
          itunesCache[normalizedSearchTerm] = {
            result: artworkUrl,
            timestamp: Date.now(),
          };

          // Devolver el resultado
          callback(artworkUrl);
        } else if (scoredResults.length > 0) {
          console.log(
            `🔍 Mejor resultado: "${scoredResults[0].result.artistName} - ${
              scoredResults[0].result.trackName
            }" - Puntaje ${scoredResults[0].score.toFixed(
              1
            )} insuficiente (>15 necesario)`
          );
          // No hay resultados suficientemente buenos, intentar siguiente variante
          tryNextVariant();
        } else {
          console.log(
            "❓ No se pudieron calcular puntuaciones para ningún resultado"
          );
          tryNextVariant();
        }
      })
      .catch((error) => {
        console.error(`❌ Error en petición a iTunes: ${error.message}`);
        console.log("🔄 Intentando método alternativo de búsqueda...");

        // Usar una URL alternativa (Itunes API con otro proxy o formato)
        const backupProxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(
          `https://itunes.apple.com/search?entity=song&limit=30&term=${encodeURIComponent(
            currentTerm
          )}`
        )}`;

        fetch(backupProxyUrl)
          .then((resp) => resp.json())
          .then((json) => {
            console.log(
              `📊 Búsqueda alternativa devolvió ${
                json.results ? json.results.length : 0
              } resultados`
            );
            if (json.results && json.results.length > 0) {
              // Evaluar resultados igual que en el método principal
              const scoredResults = json.results.map((result) => {
                // Cálculo básico de relevancia
                let score = 15; // Comenzar con el puntaje base

                // Aumentar por coincidencia directa
                if (
                  result.trackName
                    .toLowerCase()
                    .includes(currentTerm.toLowerCase()) ||
                  result.artistName
                    .toLowerCase()
                    .includes(currentTerm.toLowerCase())
                ) {
                  score += 40;
                }

                return { result, score };
              });

              // Ordenar por relevancia
              scoredResults.sort((a, b) => b.score - a.score);

              // Tomar el mejor resultado si supera el umbral
              if (scoredResults.length > 0 && scoredResults[0].score > 15) {
                const bestResult = scoredResults[0].result;
                const artworkUrl = bestResult.artworkUrl100.replace(
                  "100x100",
                  "800x800"
                );

                console.log(
                  `✅ PORTADA ENCONTRADA (método alternativo) para "${
                    bestResult.artistName
                  } - ${
                    bestResult.trackName
                  }" (Score: ${scoredResults[0].score.toFixed(1)})`
                );

                // Guardar en caché
                itunesCache[normalizedSearchTerm] = {
                  result: artworkUrl,
                  timestamp: Date.now(),
                };

                // Devolver resultado
                callback(artworkUrl);
              } else {
                console.log(
                  "❌ Resultados encontrados pero con puntuación insuficiente (<15)"
                );
                tryNextVariant();
              }
            } else {
              console.log("❌ Sin resultados en método alternativo");
              tryNextVariant();
            }
          })
          .catch((err) => {
            console.error(`❌ Error en búsqueda alternativa: ${err.message}`);
            // Error en la solicitud alternativa, intentar siguiente variante
            tryNextVariant();
          });
      });
  };

  // Palabras comunes para filtrar
  const commonStopWords = [
    "the",
    "and",
    "los",
    "las",
    "del",
    "con",
    "feat",
    "para",
    "por",
    "una",
    "mas",
    "version",
    "ft",
    "remix",
    "en",
    "de",
    "la",
    "el",
    "from",
    "live",
    "new",
    "mix",
    "official",
    "video",
    "audio",
    "music",
    "lyric",
    "lyrics",
    "full",
    "album",
    "single",
    "original",
    "a",
    "un",
    "esta",
    "este",
    "ese",
    "esa",
    "mi",
    "tu",
    "su",
    "al",
    "lo",
    "le",
    "me",
    "te",
    "se",
    "que",
    "como",
    "pero",
    "mas",
    "más",
    "tan",
    "muy",
    "ya",
    "hay",
    "así",
    "asi",
    "bien",
    "mal",
    "o",
    "e",
    "u",
    "vs",
    "vs.",
    "versus",
  ];

  // Función para probar la siguiente variante de búsqueda
  const tryNextVariant = () => {
    currentAttemptIndex++;

    if (currentAttemptIndex < searchAttempts.length) {
      // Aún hay variantes por probar
      console.log(
        `🔄 Intentando variante ${currentAttemptIndex + 1}/${
          searchAttempts.length
        }: "${searchAttempts[currentAttemptIndex]}"`
      );
      performSearch(searchAttempts[currentAttemptIndex]);
    } else {
      console.log(
        `❌ BÚSQUEDA FALLIDA: Agotadas todas las variantes (${searchAttempts.length}) para "${searchTerm}"`
      );
      // No guardamos resultados fallidos en caché para permitir reintentos
      // itunesCache[normalizedSearchTerm] = {
      //   result: null,
      //   timestamp: Date.now(),
      // };
      callback(null);
    }
  };

  // Iniciar con la primera variante
  if (searchAttempts.length > 0) {
    console.log(
      `🚀 Iniciando búsqueda con ${searchAttempts.length} variantes. Primera: "${searchAttempts[0]}"`
    );
    performSearch(searchAttempts[0]);
  } else {
    console.log("❌ No se pudieron generar variantes de búsqueda");
    // Sin variantes válidas
    itunesCache[normalizedSearchTerm] = {
      result: null,
      timestamp: Date.now(),
    };
    callback(null);
  }
};

/**
 * Parsea la información de una canción a partir de un texto
 * @param {string} songText - Texto con información de la canción
 * @returns {object} Información de cantante y canción
 */
const parseSongInfo = (songText) => {
  if (!songText || songText === "<i>Empty Title</i>") {
    return { singer: "", song: "" };
  }

  // Primero intentamos con el formato " - " (guion con espacios)
  if (songText.includes(" - ")) {
    let songArray = songText.split(" - ");
    let rawSinger = songArray[0].trim();
    let rawSong = songArray[1].trim();

    return {
      singer: cleanTitle(rawSinger),
      song: cleanTitle(rawSong),
      originalSinger: rawSinger,
      originalSong: rawSong,
    };
  }
  // Si no encontramos el formato con espacios, intentamos con guion simple
  else if (songText.includes("-")) {
    // Para evitar problemas con artistas que tienen guion en su nombre (como A-Ha),
    // verificamos si hay al menos un espacio antes o después del guion
    const guionIndex = songText.indexOf("-");
    const hasSpaceBefore = guionIndex > 0 && songText[guionIndex - 1] === " ";
    const hasSpaceAfter =
      guionIndex < songText.length - 1 && songText[guionIndex + 1] === " ";

    if (hasSpaceBefore || hasSpaceAfter) {
      let songArray = songText.split("-");
      let rawSinger = songArray[0].trim();
      let rawSong = songArray[1].trim();

      return {
        singer: cleanTitle(rawSinger),
        song: cleanTitle(rawSong),
        originalSinger: rawSinger,
        originalSong: rawSong,
      };
    }
  }

  // Si no tiene guion o el guion parece ser parte del nombre (como en A-Ha),
  // consideramos todo como nombre de canción
  const rawSong = songText.trim();
  return {
    singer: "",
    song: cleanTitle(rawSong),
    originalSong: rawSong,
  };
};

/**
 * Obtiene la imagen de la estación
 * @param {string} stationName - Nombre de la estación
 * @returns {string} URL de la imagen de la estación
 */
const getStationImageUrl = (stationName) => {
  return `https://audio.streaminghd.cl:2020/pub/${stationName}/background.jpg`;
};

// Cache para evitar precargar la misma imagen múltiples veces
const preloadedImages = {};

/**
 * Precarga la imagen de fondo de una estación para mejorar el rendimiento
 * @param {string} stationName - Nombre de la estación
 * @returns {Promise<boolean>} Promise que se resuelve con true si la imagen se cargó correctamente, false en caso contrario
 */
export const preloadStationBackground = (stationName) => {
  return new Promise((resolve) => {
    if (!stationName) {
      resolve(false);
      return;
    }

    // Verificar si ya precargamos esta imagen
    if (preloadedImages[stationName]) {
      resolve(preloadedImages[stationName]);
      return;
    }

    const imageUrl = getStationImageUrl(stationName);

    // Crear etiqueta de precarga en el head una sola vez
    const existingLink = document.head.querySelector(
      `link[href="${imageUrl}"]`
    );
    if (!existingLink) {
      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "image";
      link.href = imageUrl;
      link.fetchpriority = "high";
      document.head.appendChild(link);
    }

    // Crear una imagen con prioridad reducida para verificar carga completa
    const img = new Image();

    // Usar un timeout para no bloquear la carga si tarda demasiado
    const timeout = setTimeout(() => {
      preloadedImages[stationName] = false;
      resolve(false);
    }, 3000); // 3 segundos máximo de espera

    img.onload = () => {
      clearTimeout(timeout);
      preloadedImages[stationName] = true;
      resolve(true);
    };

    img.onerror = () => {
      clearTimeout(timeout);
      preloadedImages[stationName] = false;
      resolve(false);
    };

    // Comenzar a cargar la imagen
    img.src = imageUrl;
  });
};

// Cache para almacenar portadas
const coverCache = {};

// Función para determinar si se debe resetear la caché
const shouldResetCache = () => {
  const now = new Date();
  const storedDate = localStorage.getItem("last_cache_reset_date");

  // Si no hay fecha almacenada o es un día diferente, resetear
  if (!storedDate) {
    localStorage.setItem("last_cache_reset_date", now.toDateString());
    return true;
  }

  // Comparar si es un día diferente
  if (storedDate !== now.toDateString()) {
    localStorage.setItem("last_cache_reset_date", now.toDateString());
    return true;
  }

  return false;
};

// Limpiar caché de portadas nulas (sin resultados) al iniciar
const resetNullCovers = () => {
  // Solo limpiar entradas con valor null
  Object.keys(coverCache).forEach((key) => {
    if (coverCache[key] === null) {
      delete coverCache[key];
    }
  });

  // También limpiar resultados nulos en la caché de iTunes
  Object.keys(itunesCache).forEach((key) => {
    if (itunesCache[key] && itunesCache[key].result === null) {
      delete itunesCache[key];
    }
  });
};

// Ejecutar al cargar el archivo
if (shouldResetCache()) {
  console.log("🧹 Reseteando caché de portadas nulas");
  resetNullCovers();
}

// Ejecutar siempre al cargar para limpiar resultados nulos específicos
resetNullCovers();

// Cache para almacenar temporalmente las canciones por puerto
const songCache = {
  lastFetch: {},
  data: {},
};

/**
 * Analiza y convierte la hora del servidor según sea necesario.
 * Si la hora del servidor ya está en formato local (similar a la hora del sistema), la deja sin cambios.
 * Si la hora parece estar en UTC, la convierte al horario de Chile (UTC-3 o UTC-4 según la época del año).
 * @param {string} serverTime - Hora en formato "HH:MM:SS" recibida del servidor
 * @returns {string} Hora ajustada o sin cambios según sea necesario
 */
const convertToChileTime = (() => {
  // Cache para el offset actual, se recalcula cada hora
  let cachedOffset = null;
  let lastOffsetCheck = 0;

  return (serverTime) => {
    if (!serverTime) return serverTime;

    // Extraer las partes de la hora (formato típico: "12:34:56")
    const timeParts = serverTime.split(":");
    if (timeParts.length !== 3) return serverTime; // Formato inválido, devolver sin cambios

    // Convertir la hora del servidor a un objeto de hora
    const serverHours = parseInt(timeParts[0], 10);
    const serverMinutes = parseInt(timeParts[1], 10);
    const serverSeconds = parseInt(timeParts[2], 10);

    // Obtener la hora local del sistema del usuario
    const now = new Date();
    const localHours = now.getHours();
    const localMinutes = now.getMinutes();

    // Calcular la diferencia absoluta en minutos entre la hora del servidor y la hora local
    const serverTotalMinutes = serverHours * 60 + serverMinutes;
    const localTotalMinutes = localHours * 60 + localMinutes;

    // Calcular la diferencia absoluta mínima (normalizada a ±12 h) entre la hora del servidor y la hora local
    const rawDiffMinutes = serverTotalMinutes - localTotalMinutes;
    // Normalizar al rango -720..720 minutos (±12 h) para evitar problemas al cruzar medianoche
    const normalizedDiffMinutes = ((rawDiffMinutes + 720) % 1440) - 720;
    const absNormalizedDiff = Math.abs(normalizedDiffMinutes);

    // Umbral para decidir si la hora está en UTC
    // Consideramos que si la diferencia está dentro de ±30 minutos del offset oficial de Chile (3h o 4h),
    // entonces probablemente la hora sea UTC y necesite conversión.
    const isLikelyUTC = Math.abs(absNormalizedDiff - cachedOffset * 60) < 30;

    // Si NO parece UTC, devolvemos la hora tal cual porque ya está en horario local
    if (!isLikelyUTC) {
      console.log(
        "🕒 Hora del servidor parece estar ya en formato local, no se convierte"
      );
      return serverTime;
    }

    // Caso contrario, asumimos que es UTC y continuamos con la conversión
    console.log(
      "🕒 Hora del servidor parece estar en UTC, convirtiendo a hora local"
    );

    // Verificar si necesitamos recalcular el offset (cada hora para evitar cálculos constantes)
    const currentTime = Date.now();
    if (cachedOffset === null || currentTime - lastOffsetCheck > 3600000) {
      // 1 hora
      // Obtener la fecha actual para determinar si Chile está en horario de verano
      const today = new Date();
      const month = today.getMonth() + 1; // getMonth() devuelve 0-11
      const day = today.getDate();

      // Determinar offset: UTC-3 (horario verano) o UTC-4 (horario invierno)
      // Simplificación: septiembre a marzo es verano, abril a agosto es invierno
      // excepto los primeros días de abril que aún es verano
      if (month >= 9 || month <= 3 || (month === 4 && day < 24)) {
        cachedOffset = 3; // Horario de verano (UTC-3)
      } else {
        cachedOffset = 4; // Horario de invierno (UTC-4)
      }

      lastOffsetCheck = currentTime;
    }

    // Ajustar las horas restando el offset de Chile
    const adjustedHours = (serverHours - cachedOffset + 24) % 24;

    // Formatear la hora en formato HH:MM:SS
    const formattedHours = adjustedHours.toString().padStart(2, "0");
    const formattedMinutes = serverMinutes.toString().padStart(2, "0");
    const formattedSeconds = serverSeconds.toString().padStart(2, "0");

    return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
  };
})();

// Tiempo mínimo entre solicitudes al mismo puerto (5 segundos)
const MIN_FETCH_INTERVAL = 5000;

/**
 * Comprueba si hay una canción nueva en el servidor
 * @param {string} port - Puerto del servidor de streaming
 * @param {string} currentSinger - Cantante actual
 * @param {string} currentSong - Canción actual
 * @returns {Promise} Promesa con la información de la nueva canción
 */
export const isNewSong = (port, currentSinger, currentSong) => {
  console.log(
    `🎵 isNewSong verificando: puerto=${port}, cantante="${currentSinger}", canción="${currentSong}"`
  );

  return new Promise((resolve, reject) => {
    // Comprobar si ya tenemos datos en caché y si han pasado menos de 5 segundos
    const now = Date.now();
    const lastFetchTime = songCache.lastFetch[port] || 0;

    if (now - lastFetchTime < MIN_FETCH_INTERVAL && songCache.data[port]) {
      const cachedData = songCache.data[port];
      console.log(`⏱️ Usando caché reciente para puerto ${port}`);

      // Si la canción en caché es diferente de la actual, resolver con los datos en caché
      if (
        cachedData.singer !== cleanTitle(currentSinger) ||
        cachedData.song !== cleanTitle(currentSong)
      ) {
        console.log(
          `✅ Canción nueva detectada en caché: ${cachedData.singer} - ${cachedData.song}`
        );
        resolve(cachedData);
      } else {
        console.log(`❌ No hay canción nueva en caché`);
        reject(new Error("No hay canción nueva"));
      }
      return;
    }

    // Si no hay datos en caché o ha pasado suficiente tiempo, hacer la solicitud
    let currentSongUrl = `http://audio.streaminghd.cl:${port}/currentsong`;
    console.log(`🔍 Consultando canción actual en: ${currentSongUrl}`);

    const { url, fetchOptions } = getProxyUrl(currentSongUrl);

    fetch(url, fetchOptions)
      .then((response) => {
        console.log(`📥 Respuesta recibida tipo: ${response.type}`);
        if (response.type === "opaque") {
          // No podemos leer el contenido debido a no-cors
          console.error(
            "❌ Respuesta opaca (CORS). No se puede leer el contenido"
          );
          reject(new Error("Respuesta opaca"));
          return;
        }

        return response.text().then((value) => {
          console.log(`📄 Texto recibido: "${value}"`);
          if (!value || value === "<i>Empty Title</i>") {
            const emptyResult = { song: "", singer: "" };
            console.log("❌ Respuesta vacía o Empty Title");
            // Actualizar caché
            songCache.data[port] = emptyResult;
            songCache.lastFetch[port] = now;
            resolve(emptyResult);
          } else {
            const songInfo = parseSongInfo(value);
            console.log(`✅ Canción parseada: ${JSON.stringify(songInfo)}`);

            // Actualizar caché
            songCache.data[port] = songInfo;
            songCache.lastFetch[port] = now;

            // Para la comparación, usar los valores limpios
            if (
              songInfo.singer !== cleanTitle(currentSinger) ||
              songInfo.song !== cleanTitle(currentSong)
            ) {
              console.log(`✅ Canción nueva detectada`);
              resolve(songInfo);
            } else {
              console.log(`❌ La canción no ha cambiado`);
              reject(new Error("No hay canción nueva"));
            }
          }
        });
      })
      .catch((error) => {
        console.error(`❌ Error al obtener canción: ${error.message}`);
        reject(error);
      });
  });
};

/**
 * Obtiene las canciones anteriores reproducidas
 * @param {string} port - Puerto del servidor de streaming
 * @returns {Promise} Promesa con el listado de canciones previas
 */
export const getPreviousSongs = (port) => {
  return new Promise((resolve, reject) => {
    // Verificar si hay canciones previas en caché
    const cacheKey = `previous_songs_${port}`;
    const cachedSongs = sessionStorage.getItem(cacheKey);

    // Determina si debemos forzar la actualización de portadas
    const forceUpdate =
      sessionStorage.getItem("force_update_covers") === "true";

    // Si se requiere actualización forzada, eliminar marca después de usarla
    if (forceUpdate) {
      sessionStorage.removeItem("force_update_covers");
      console.log("🔄 Actualización forzada de portadas activada");
    }

    // Si hay datos en caché con menos de 5 minutos de antigüedad y no es actualización forzada, usarlos
    if (cachedSongs && !forceUpdate) {
      try {
        const { songs, timestamp } = JSON.parse(cachedSongs);
        const now = Date.now();

        // Usar caché si tiene menos de 30 segundos (30,000 ms)
        if (now - timestamp < 30000) {
          // Verificar si hay canciones sin portada que podríamos actualizar
          let hasMissingSongs = false;
          const songsWithCovers = [...songs];

          // Si pasó al menos 1 minuto, intentar buscar portadas faltantes
          if (now - timestamp > 60000) {
            // Programar búsqueda de portadas faltantes en segundo plano
            setTimeout(async () => {
              try {
                for (let i = 0; i < songsWithCovers.length; i++) {
                  if (
                    !songsWithCovers[i].cover ||
                    songsWithCovers[i].cover ===
                      getStationImageUrl(getStationNameFromUrl())
                  ) {
                    console.log(
                      `🔎 Reintentando búsqueda de portada para: ${songsWithCovers[i].singer} - ${songsWithCovers[i].song}`
                    );
                    try {
                      // Intentar obtener portada solo para canciones sin portada
                      const cover = await getCover({
                        singer: songsWithCovers[i].singer,
                        song: songsWithCovers[i].song,
                      });
                      songsWithCovers[i].cover = cover;
                      hasMissingSongs = true;
                    } catch (err) {
                      console.error("Error reintentando portada:", err);
                    }
                  }
                }

                // Si encontramos portadas, actualizar la caché
                if (hasMissingSongs) {
                  sessionStorage.setItem(
                    cacheKey,
                    JSON.stringify({
                      songs: songsWithCovers,
                      timestamp: now,
                    })
                  );
                }
              } catch (error) {
                console.error(
                  "Error en actualización de portadas en segundo plano:",
                  error
                );
              }
            }, 100); // Esperar 100ms para no bloquear la interfaz
          }

          resolve(songs);
          return;
        }
      } catch (e) {
        // Error al parsear la caché, ignorar y continuar con la petición
        console.error("Error al leer caché de canciones previas", e);
      }
    }

    // Si no hay caché válida, hacer la solicitud
    let previousSongsUrl = `http://audio.streaminghd.cl:${port}/played.html`;
    const { url } = getProxyUrl(previousSongsUrl);

    fetch(url)
      .then((response) => {
        // Si la respuesta no es correcta, intentar proxy alternativo
        if (!response.ok || response.status >= 400) {
          throw new Error(`Status HTTP ${response.status}`);
        }
        return response.text();
      })
      .catch(async (primaryError) => {
        console.warn(
          `⚠️ Primer intento falló (${primaryError.message}). Intentando con proxy alternativo...`
        );

        try {
          const backupUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(
            previousSongsUrl
          )}`;
          const backupResponse = await fetch(backupUrl);
          if (!backupResponse.ok) {
            throw new Error(`Backup status ${backupResponse.status}`);
          }
          return backupResponse.text();
        } catch (backupError) {
          // Propagar el error final para ser capturado por el manejador general
          throw backupError;
        }
      })
      .then((html) => {
        let parser = new DOMParser();
        let document = parser.parseFromString(html, "text/html");
        let table = document.getElementsByTagName("table")[1];

        // Si no se encuentra la tabla, devolver array vacío
        if (!table) {
          const emptyResults = [];
          // Guardar en caché
          sessionStorage.setItem(
            cacheKey,
            JSON.stringify({
              songs: emptyResults,
              timestamp: Date.now(),
            })
          );
          resolve(emptyResults);
          return;
        }

        let rows = table.getElementsByTagName("tr");
        let previousSongs = [];

        // Limitar a un máximo de 20 canciones (cambiado de 10 a 20)
        const maxSongs = Math.min(rows.length - 1, 20);

        for (let i = 1; i <= maxSongs; i++) {
          let songText = htmlDecode(
            rows[i].getElementsByTagName("td")[1].innerHTML
          );
          let time = htmlDecode(
            rows[i].getElementsByTagName("td")[0].innerHTML
          );

          // Convertir tiempo de UTC a hora de Chile
          const chileTime = convertToChileTime(time);

          const songInfo = parseSongInfo(songText);
          previousSongs.push({
            ...songInfo,
            time: chileTime,
            cover: null,
          });
        }

        // Si no hay canciones anteriores, resolver inmediatamente
        if (previousSongs.length === 0) {
          // Guardar en caché
          sessionStorage.setItem(
            cacheKey,
            JSON.stringify({
              songs: previousSongs,
              timestamp: Date.now(),
            })
          );
          resolve(previousSongs);
          return;
        }

        // Obtener portadas secuencialmente para evitar sobrecarga
        const fetchCovers = async () => {
          for (let j = 0; j < previousSongs.length; j++) {
            try {
              // Obtener la portada para cada canción (secuencialmente)
              const cover = await getCover({
                singer: previousSongs[j].singer,
                song: previousSongs[j].song,
              });
              previousSongs[j].cover = cover;

              // Si es la última iteración, guardar en caché y resolver
              if (j === previousSongs.length - 1) {
                // Guardar la lista completa en caché
                sessionStorage.setItem(
                  cacheKey,
                  JSON.stringify({
                    songs: previousSongs,
                    timestamp: Date.now(),
                  })
                );

                resolve(previousSongs);
              }
            } catch (error) {
              console.error("Error al obtener portada", error);
            }
          }
        };

        // Iniciar la obtención de portadas secuencialmente
        fetchCovers();
      })
      .catch((error) => {
        reject(error);
      });
  });
};

/**
 * Obtiene el nombre de la estación actual de la URL
 * @returns {string} Nombre de la estación
 */
const getStationNameFromUrl = () => {
  const path = window.location.pathname;
  const parts = path.split("/");
  return parts[parts.length - 1];
};

/**
 * Obtiene la URL de la carátula para una canción o artista
 * @param {object} input - Objeto con información de la canción y artista
 * @param {string} [input.singer] - Nombre del artista/cantante
 * @param {string} [input.song] - Nombre de la canción
 * @returns {Promise<string>} Promesa con la URL de la carátula
 */
export const getCover = (input) => {
  console.log(`🖼️ getCover iniciado con: ${JSON.stringify(input)}`);

  return new Promise((resolve) => {
    // Extraer la información
    let singer = input && input.singer ? input.singer.trim() : "";
    let song = input && input.song ? input.song.trim() : "";

    console.log(`🎤 Artista original: "${singer}"`);
    console.log(`🎵 Canción original: "${song}"`);

    // Obtener versiones originales para usar en la búsqueda
    const originalSinger = singer;
    const originalSong = song;

    // Guardar versiones limpias para búsqueda alternativa
    singer = cleanTitle(singer);
    song = cleanTitle(song);

    console.log(`🎤 Artista limpio: "${singer}"`);
    console.log(`🎵 Canción limpia: "${song}"`);

    // Clave de caché para esta combinación de canción y artista
    const cacheKey = `${originalSinger}:${originalSong}`.toLowerCase();

    console.log(`🔑 Clave de caché: "${cacheKey}"`);

    // Verificar si ya tenemos esta portada en caché (solo si no es nula)
    if (coverCache[cacheKey] && coverCache[cacheKey] !== null) {
      console.log(`✅ Portada encontrada en caché`);
      resolve(coverCache[cacheKey]);
      return;
    }

    // Obtener el nombre de la estación de la URL
    const path = window.location.pathname;
    const parts = path.split("/");
    const stationName = parts[parts.length - 1];

    console.log(`📻 Estación: "${stationName}"`);

    // URLs para las imágenes
    const stationImageUrl = getStationImageUrl(stationName);

    console.log(`🖼️ URL de imagen de estación: ${stationImageUrl}`);

    // CASO ESPECIAL: Si parece una cuña de radio, ir directamente a imagen de la estación
    if (isBroadcastAnnouncement(singer, song)) {
      console.log(`📢 Detectada cuña de radio, usando imagen de estación`);
      // Guardar URL en cookie para que PHP pueda acceder
      document.cookie = `current_cover_url=${encodeURIComponent(
        stationImageUrl
      )}; path=/; max-age=86400; SameSite=Lax`;

      // Guardar en caché para futuras consultas
      coverCache[cacheKey] = stationImageUrl;
      resolve(stationImageUrl);
      return;
    }

    // Construir consulta inteligente con varias opciones
    const buildSmartQuery = () => {
      // Si tenemos artista y canción, probar con la consulta completa original
      if (originalSinger && originalSong) {
        return `${originalSinger} ${originalSong}`;
      }
      // Si solo tenemos canción, usarla como está
      else if (originalSong) {
        return originalSong;
      }
      // Si solo tenemos artista, usarlo como está
      else if (originalSinger) {
        return originalSinger;
      }
      // Si no tenemos ni artista ni canción, devolver null
      return "";
    };

    const searchQuery = buildSmartQuery();

    // Si no tenemos información suficiente, usar imagen de la estación
    if (!searchQuery) {
      // Guardar URL en cookie para que PHP pueda acceder
      document.cookie = `current_cover_url=${encodeURIComponent(
        stationImageUrl
      )}; path=/; max-age=86400; SameSite=Lax`;

      // Guardar en caché para futuras consultas
      coverCache[cacheKey] = stationImageUrl;
      resolve(stationImageUrl);
      return;
    }

    // Buscar portada usando el algoritmo avanzado
    searchItunesAndResolve(searchQuery, (itunesImage) => {
      // Determinar la imagen final a usar
      const finalImage = itunesImage || stationImageUrl;

      // Guardar URL en cookie para que PHP pueda acceder
      document.cookie = `current_cover_url=${encodeURIComponent(
        finalImage
      )}; path=/; max-age=86400; SameSite=Lax`;

      // Solo guardar en caché si encontramos una imagen real (no la estación por defecto)
      if (itunesImage) {
        coverCache[cacheKey] = finalImage;
      } else {
        // Si no encontramos imagen, guardar null para reintentarlo después
        coverCache[cacheKey] = null;
      }

      resolve(finalImage);
    });
  });
};

/**
 * Crea y configura elementos de audio con manejo de errores
 * @param {string} port - Puerto del servidor de audio
 * @param {string} name - Nombre de la estación
 * @param {function} setAudio - Función para establecer el audio en el estado
 * @param {function} setAudioError - Función para establecer error de audio
 * @returns {Audio} Elemento de audio configurado
 */
export const createAudioElement = (port, name, setAudio, setAudioError) => {
  // URL principal que funciona correctamente
  const audioUrl = `https://audio.streaminghd.cl:2000/stream/${name}/;stream.mp3`;
  const audioElement = new Audio(audioUrl);
  audioElement.crossOrigin = "anonymous";
  audioElement.preload = "auto";

  // Control de fallos
  audioElement.addEventListener("error", () => {
    if (setAudioError) setAudioError(true);
  });

  // La URL principal funcionó
  audioElement.addEventListener("canplay", () => {
    if (setAudioError) setAudioError(false);
  });

  // Configurar manejadores de MediaSession si está disponible
  if ("mediaSession" in navigator) {
    // Configurar manejador de reproducción
    navigator.mediaSession.setActionHandler("play", () => {
      audioElement
        .play()
        .then(() => {
          navigator.mediaSession.playbackState = "playing";
        })
        .catch((error) => console.error("Error al reproducir:", error));
    });

    // Configurar manejador de pausa
    navigator.mediaSession.setActionHandler("pause", () => {
      audioElement.pause();
      navigator.mediaSession.playbackState = "paused";
    });

    // Configurar estado inicial
    navigator.mediaSession.playbackState = "paused";
  }

  return audioElement;
};

/**
 * Función auxiliar para generar URL de compartir
 * @param {string} port - Puerto del servidor
 * @param {string} name - Nombre de la estación
 * @returns {string} URL para compartir
 */
const getShareUrl = (port, name) => {
  return `https://player.streaminghd.cl/audio/${port}/${name}`;
};

/**
 * Genera el texto para compartir según los datos disponibles
 * @param {string} name - Nombre de la estación
 * @param {string} song - Canción (opcional)
 * @param {string} singer - Cantante (opcional)
 * @returns {string} Texto formateado para compartir
 */
const getShareText = (name, song, singer) => {
  if (song && singer) {
    return `Escucha ${song} de ${singer} - ${name}`;
  } else if (song) {
    return `Escucha ${song} - ${name}`;
  } else {
    return `Escucha ${name}`;
  }
};

/**
 * Genera el texto para compartir en Facebook con un formato más personal
 * @param {string} name - Nombre de la estación
 * @param {string} song - Canción (opcional)
 * @param {string} singer - Cantante (opcional)
 * @returns {string} Texto personalizado para compartir en Facebook
 */
const getFacebookShareText = (name, song, singer) => {
  if (song && singer) {
    return `Estoy escuchando ${song} de ${singer} en ${name}`;
  } else if (song) {
    return `Estoy escuchando ${song} en ${name}`;
  } else {
    return `Estoy escuchando ${name}`;
  }
};

/**
 * Funciones para compartir en redes sociales
 */
export const shareServices = {
  emailShare: (port, name, song, singer) => {
    let shareUrl = getShareUrl(port, name);
    let mailShareUrl = `mailto:?subject=Escucha esta radio&body=`;
    let shareText = getShareText(name, song, singer);
    window.open(`${mailShareUrl}${shareText} - ${shareUrl}`);
  },

  twitterShare: (port, name, song, singer) => {
    let shareUrl = getShareUrl(port, name);
    let twitterShareUrl = `https://twitter.com/intent/tweet?url=${shareUrl}&text=`;
    let shareText = getShareText(name, song, singer);
    window.open(
      `${twitterShareUrl}${shareText}`,
      ``,
      `width=600,height=400,left=200,top=200`
    );
  },

  facebookShare: (port, name, song, singer) => {
    // Usar la URL actual ya que contiene los metadatos actualizados
    let shareUrl = window.location.href;
    let shareText = getFacebookShareText(name, song, singer);

    // Usar el método más simple y actual para compartir en Facebook
    // El parámetro 'quote' permite precompletar el texto que verá el usuario
    let facebookShareUrl = `https://www.facebook.com/sharer.php?u=${encodeURIComponent(
      shareUrl
    )}&quote=${encodeURIComponent(shareText)}`;

    window.open(facebookShareUrl, "", "width=600,height=400,left=200,top=200");
  },

  telegramShare: (port, name, song, singer) => {
    // Usar la URL actual ya que contiene los metadatos actualizados
    let shareUrl = window.location.href;
    let telegramShareUrl = `https://telegram.me/share/?url=${shareUrl}&text=`;
    let shareText = getShareText(name, song, singer);
    window.open(
      `${telegramShareUrl}${shareText}`,
      ``,
      `width=600,height=400,left=200,top=200`
    );
  },

  whatsappShare: (port, name, song, singer) => {
    // Usar la URL actual ya que contiene los metadatos actualizados
    let shareUrl = window.location.href;
    let whatsappShareUrl = `https://wa.me/?text=`;
    let shareText = getShareText(name, song, singer);
    window.open(
      `${whatsappShareUrl}${encodeURIComponent(
        shareText
      )} - ${encodeURIComponent(shareUrl)}`,
      ``,
      `width=600,height=400,left=200,top=200`
    );
  },
};

/**
 * Trunca un texto a un número máximo de caracteres y añade puntos suspensivos cuando es necesario
 * @param {string} text - El texto a truncar
 * @param {number} maxLength - Longitud máxima permitida (por defecto 30)
 * @returns {string} Texto truncado con puntos suspensivos si es necesario
 */
export const truncateText = (text, maxLength = 30) => {
  if (!text) return "";

  // Si el texto es menor o igual que la longitud máxima, lo devolvemos tal cual
  if (text.length <= maxLength) return text;

  // Si no, truncamos y añadimos puntos suspensivos
  return text.substring(0, maxLength - 3) + "...";
};

/**
 * Actualiza los metadatos de MediaSession para mostrar información en la pantalla de bloqueo
 * @param {string} title - Título de la canción
 * @param {string} artist - Nombre del artista
 * @param {string} album - Nombre del álbum o estación
 * @param {string} artworkUrl - URL de la imagen de portada
 * @returns {boolean} Verdadero si MediaSession está disponible
 */
export const updateMediaSession = (title, artist, album, artworkUrl) => {
  // Verificar si la API MediaSession está disponible
  if (!("mediaSession" in navigator)) {
    return false;
  }

  // Configurar los metadatos
  navigator.mediaSession.metadata = new MediaMetadata({
    title: title || album,
    artist: artist || "",
    album: album || "",
    artwork: [
      { src: artworkUrl, sizes: "96x96", type: "image/jpeg" },
      { src: artworkUrl, sizes: "128x128", type: "image/jpeg" },
      { src: artworkUrl, sizes: "192x192", type: "image/jpeg" },
      { src: artworkUrl, sizes: "256x256", type: "image/jpeg" },
      { src: artworkUrl, sizes: "384x384", type: "image/jpeg" },
      { src: artworkUrl, sizes: "512x512", type: "image/jpeg" },
    ],
  });

  return true;
};

/**
 * Convierte el texto a mayúsculas, útil para mostrar nombres de estaciones
 * @param {string} text - Texto a convertir
 * @returns {string} Texto en mayúsculas
 */
export const toUpperCase = (text) => {
  if (!text) return "";
  return text.toUpperCase();
};

/**
 * Función para actualizar periódicamente el historial de canciones
 * @param {string} port - Puerto del servidor de streaming
 * @param {function} setHistoryCallback - Función para actualizar el estado con las canciones
 * @param {number} intervalMs - Intervalo de actualización en milisegundos (default: 30000 = 30 segundos)
 * @returns {number} ID del intervalo para poder cancelarlo si es necesario
 */
export const startHistoryUpdater = (
  port,
  setHistoryCallback,
  intervalMs = 30000
) => {
  // Primero, obtenemos el historial inmediatamente
  getPreviousSongs(port).then(
    (songs) => {
      setHistoryCallback(songs);
    },
    (error) => {
      console.error("Error al obtener historial inicial:", error);
    }
  );

  // Luego, configuramos una actualización periódica
  const intervalId = setInterval(() => {
    getPreviousSongs(port).then(
      (songs) => {
        setHistoryCallback(songs);
      },
      (error) => {
        console.error("Error al actualizar historial:", error);
      }
    );
  }, intervalMs);

  // Devolvemos el ID del intervalo para que pueda ser limpiado cuando sea necesario
  return intervalId;
};

/**
 * Detiene la actualización periódica del historial
 * @param {number} intervalId - ID del intervalo a detener
 */
export const stopHistoryUpdater = (intervalId) => {
  if (intervalId) {
    clearInterval(intervalId);
  }
};

/**
 * Fuerza actualización de portadas al recargar
 * Esto se llama automáticamente, no es necesario un botón
 */
(() => {
  // Ejecutar al cargar la página
  if (typeof window !== "undefined") {
    // Añadir evento para detectar recarga de página
    window.addEventListener("load", () => {
      console.log(
        "🔄 Verificando si necesitamos actualizar portadas al recargar"
      );

      // Obtener tiempo desde última visita
      const lastVisit = sessionStorage.getItem("last_visit_timestamp");
      const now = Date.now();

      // Guardar tiempo actual
      sessionStorage.setItem("last_visit_timestamp", now);

      // Si es la primera visita o ha pasado más de 5 minutos, forzar actualización
      if (!lastVisit || now - parseInt(lastVisit, 10) > 300000) {
        console.log(
          "🔄 Forzando actualización de portadas por recarga de página"
        );
        sessionStorage.setItem("force_update_covers", "true");

        // También limpiamos la caché de portadas nulas
        resetNullCovers();
      }
    });
  }
})();

/**
 * Añade una opción para regenerar todas las portadas al actualizar la página
 * Especialmente útil para canciones incorrectamente detectadas como cuñas
 */
(() => {
  if (typeof window !== "undefined") {
    window.addEventListener("load", () => {
      // Buscar en el historial canciones que pudieran ser cuñas incorrectamente detectadas
      setTimeout(() => {
        try {
          // Obtener el puerto de la URL
          const getPortFromUrl = () => {
            const pathMatch = window.location.pathname.match(/\/audio\/(\d+)/);
            if (pathMatch && pathMatch[1]) {
              return pathMatch[1];
            }
            return null;
          };

          const port = getPortFromUrl();
          if (!port) return;

          const cacheKey = `previous_songs_${port}`;
          const cachedSongs = sessionStorage.getItem(cacheKey);

          if (cachedSongs) {
            const { songs } = JSON.parse(cachedSongs);
            let needsUpdate = false;

            // Buscar canciones con portada de estación
            const stationImageUrl = getStationImageUrl(getStationNameFromUrl());

            for (let i = 0; i < songs.length; i++) {
              // Si la canción tiene artista y título pero tiene la portada de la estación
              if (
                songs[i].singer &&
                songs[i].song &&
                songs[i].cover === stationImageUrl
              ) {
                console.log(
                  `🔍 Revisión de canción potencialmente mal identificada: "${songs[i].singer} - ${songs[i].song}"`
                );

                // Verificar que no sea realmente una cuña
                const correctCriteria =
                  songs[i].singer &&
                  songs[i].song &&
                  !isBroadcastAnnouncementLegacy(
                    songs[i].singer,
                    songs[i].song
                  );

                if (correctCriteria) {
                  needsUpdate = true;
                  console.log(
                    `🔄 Marcando para regeneración de portada: "${songs[i].singer} - ${songs[i].song}"`
                  );

                  // Limpiar la caché para esta canción
                  const cacheKey =
                    `${songs[i].singer}:${songs[i].song}`.toLowerCase();
                  if (coverCache[cacheKey]) {
                    delete coverCache[cacheKey];
                  }
                }
              }
            }

            if (needsUpdate) {
              console.log(
                "🔄 Se encontraron canciones mal identificadas, forzando actualización del historial"
              );
              sessionStorage.setItem("force_update_covers", "true");
            }
          }
        } catch (error) {
          console.error(
            "Error al verificar canciones mal identificadas:",
            error
          );
        }
      }, 2000); // Esperar 2 segundos después de cargar para permitir que el historial se cargue primero
    });
  }
})();

/**
 * Versión anterior de la función para detectar cuñas (para comparación)
 * @private Solo para uso interno
 */
const isBroadcastAnnouncementLegacy = (singer, song) => {
  if (!singer && !song) return true;

  // Patrones comunes en cuñas de radio
  const patterns = [
    "marca",
    "escucha",
    "sonido",
    "adulto",
    "radio",
    "fm",
    "transmite",
  ];

  // Texto combinado para buscar patrones
  const combinedText = `${singer} ${song}`.toLowerCase();

  // Si el texto es muy largo, probablemente sea una cuña
  if (combinedText.length > 40) {
    return true;
  }

  // Buscar patrones de cuña
  for (const pattern of patterns) {
    if (combinedText.includes(pattern)) {
      return true;
    }
  }

  return false;
};
